import { type PermissionGroup, PermissionGroupUser } from '@kalos/kalos-rpc';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useConfirm,
} from '@kalos/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { queryKeys } from '../../../hooks/react-query/constants';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import { UserClientService } from '../../../tools/helpers';
import { AddPermissionsDialog } from './AddPermissionsDialog';
import { PERMISSION_SECTIONS, type PermissionSection } from './permissionSections';

interface Props {
  userId: number;
  onClose: () => void;
}

export const UserPermissionsDialog = ({ userId, onClose }: Props) => {
  const auth = useAuth();
  const isOwnerSU = useMemo(
    () => !!auth.user.permissionGroups.find((p) => p.name === 'SU'),
    [auth.user.permissionGroups],
  );
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [addSection, setAddSection] = useState<PermissionSection>();
  const [openSections, setOpenSections] = useState<string[]>([]);

  const userQuery = useUserQuery({ filters: { id: userId } });
  const user = userQuery.data;
  const permissionGroups = useMemo(() => user?.permissionGroups ?? [], [user]);

  const removeMutation = useMutation({
    mutationFn: async (groups: PermissionGroup[]) => {
      for (const group of groups) {
        const req = PermissionGroupUser.create();
        req.userId = userId;
        req.permissionGroupId = group.id;
        await UserClientService.RemoveUserFromPermissionGroup(req);
      }
    },
    onSuccess: (_, groups) => {
      toast({ title: `Removed ${groups.length} permission${groups.length === 1 ? '' : 's'}` });
    },
    onError: () => {
      toast({
        title: 'Failed to remove permissions',
        description: 'Some permissions may not have been removed.',
        variant: 'destructive',
      });
    },
    onSettled: async () => {
      setSelectedIds(new Set());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.user.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.user.permissionGroupUser.root] }),
      ]);
    },
  });

  // Removing a role or the SU privilege is restricted to SUs
  const canRemove = (group: PermissionGroup) =>
    isOwnerSU || (group.type !== 'role' && group.name !== 'SU');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return permissionGroups;
    return permissionGroups.filter(
      (p) => p.name.toLowerCase().includes(query) || p.description.toLowerCase().includes(query),
    );
  }, [permissionGroups, search]);

  const selectedGroups = permissionGroups.filter((group) => selectedIds.has(group.id));
  const busy = removeMutation.isPending;

  const toggleSelected = (ids: number[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const handleRemoveSelected = async () => {
    if (!user || selectedGroups.length === 0) return;
    if (
      !(await confirm(
        `Remove ${selectedGroups.length} permission${selectedGroups.length === 1 ? '' : 's'} from ${user.firstname} ${user.lastname}?`,
      ))
    ) {
      return;
    }
    removeMutation.mutate(selectedGroups);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent
        blurActiveElementOnScroll={false}
        className="max-h-[85vh] overflow-y-auto bg-white sm:max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle>
            {user ? `Permissions for ${user.firstname} ${user.lastname}` : 'Permissions'}
          </DialogTitle>
          <DialogDescription>
            Manage the roles, departments, and privileges assigned to this employee.
          </DialogDescription>
        </DialogHeader>
        {userQuery.isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        ) : userQuery.isError ? (
          <p className="text-destructive">Failed to load permissions. Please try again.</p>
        ) : (
          <TooltipProvider delayDuration={300}>
            <div className="relative max-w-sm">
              <Search className="text-muted-foreground absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search permissions..."
                className="pl-8"
              />
            </div>
            <Accordion
              type="multiple"
              value={
                search.trim()
                  ? PERMISSION_SECTIONS.filter((section) =>
                      filtered.some((p) => p.type === section.type),
                    ).map((section) => section.type)
                  : openSections
              }
              onValueChange={(value) => {
                if (!search.trim()) setOpenSections(value);
              }}
            >
              {PERMISSION_SECTIONS.map((section) => {
                const data = filtered.filter((p) => p.type === section.type);
                if (search.trim() && data.length === 0) return null;
                const selectable = data.filter(canRemove);
                const allSelected =
                  selectable.length > 0 && selectable.every((group) => selectedIds.has(group.id));
                return (
                  <AccordionItem key={section.type} value={section.type}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <AccordionTrigger className="text-base font-semibold">
                          {section.label}
                        </AccordionTrigger>
                      </div>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon-sm"
                            disabled={busy}
                            onClick={() => setAddSection(section)}
                          >
                            <Plus className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="p-1 text-xs">
                          Add {section.label.toLowerCase()}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <AccordionContent>
                      {data.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                          No {section.label.toLowerCase()} assigned.
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10">
                                <Checkbox
                                  checked={allSelected}
                                  disabled={selectable.length === 0 || busy}
                                  onCheckedChange={(checked) =>
                                    toggleSelected(
                                      selectable.map((group) => group.id),
                                      checked === true,
                                    )
                                  }
                                  aria-label={`Select all ${section.label.toLowerCase()}`}
                                />
                              </TableHead>
                              <TableHead>{section.nameHeader}</TableHead>
                              <TableHead>Description</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.map((group) => (
                              <TableRow key={group.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedIds.has(group.id)}
                                    disabled={!canRemove(group) || busy}
                                    onCheckedChange={(checked) =>
                                      toggleSelected([group.id], checked === true)
                                    }
                                    aria-label={`Select ${group.name}`}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{group.name}</TableCell>
                                <TableCell>{group.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
            {search.trim() && filtered.length === 0 && (
              <p className="text-muted-foreground">No permissions match “{search.trim()}”.</p>
            )}
          </TooltipProvider>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Close
          </Button>
          <Button
            variant="destructive"
            disabled={selectedGroups.length === 0 || busy}
            onClick={handleRemoveSelected}
          >
            {busy
              ? 'Removing...'
              : `Remove Selected${selectedGroups.length ? ` (${selectedGroups.length})` : ''}`}
          </Button>
        </DialogFooter>
        {addSection && user && (
          <AddPermissionsDialog
            userId={userId}
            userName={`${user.firstname} ${user.lastname}`}
            section={addSection}
            existingIds={permissionGroups.map((group) => group.id)}
            isOwnerSU={isOwnerSU}
            onClose={() => setAddSection(undefined)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
